/* eslint-disable class-methods-use-this */

import {last} from 'lodash';

import {decorateWithClientId} from '../commons/clientIdModel';
import {Atom, ClientAtom, ClientFunctionAtom, PreAtom} from './expressionAtomModel';
import {getPropertyLabel} from './expressionLabels';
import {brandExpressionId, Expression, ExpressionId, PreExpression} from './expressionModel';
import {ExpressionsContext} from './expressionsContext';
import {ExpressionDependencies} from './expressionState';
import {
  areInferredTypesEqual,
  InboxResult,
  InferredType,
  isListType,
  StringLikeResults,
  TagResult,
  TeammateResult,
  UnknownResult,
} from './infer/expressionResultModel';
import {buildAtoms, getAtomTransformer, getExpressionsMaxIndexByAtom, getResultType} from './infer/helpers';
import {TransformerIdsEnum} from './infer/transformers';

export {
  buildAtom,
  buildAtomDescription,
  buildAtoms,
  isTransformerId,
  TransformerIdsEnum,
} from './infer/internal';

export function parseExpression(
  preExpression: PreExpression,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  const atoms = buildAtoms(preExpression.atoms, context.initialInfer, context, dependencies);

  return buildExpression(preExpression.id, preExpression.name, atoms, context, dependencies);
}

export function buildExpression(
  id: ExpressionId,
  name: string,
  atoms: ReadonlyArray<ClientAtom>,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): Expression {
  const defaultName = buildDefaultName(atoms, context, dependencies) ?? buildPlaceholderName(context);
  const lastAtom = last(atoms);
  const inferredType = lastAtom ? lastAtom.inferredType : UnknownResult;

  return {
    id,
    name: name !== defaultName ? name : '',
    defaultName,
    atoms,
    inferredType,
  };
}

export function buildPropertyPreAtom(property: string): PreAtom {
  return {transform: TransformerIdsEnum.GET, params: {property}};
}

export function buildFunctionStartAtom(previousInfer: InferredType): ClientAtom {
  return decorateWithClientId({
    transform: TransformerIdsEnum.FUNCTION_START,
    previousInfer,
    params: {},
    inferredType: previousInfer,
    isValid: true,
  });
}

export function inferAtom(
  atom: Atom,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): InferredType {
  const transformer = getAtomTransformer(atom);
  return transformer.inferType(atom, context, dependencies);
}

export function buildDefaultName(
  atoms: ReadonlyArray<ClientAtom>,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): string | undefined {
  const lastAtom = last(atoms);
  if (!lastAtom) {
    return undefined;
  }

  const lastDescription = buildDefaultAtomName(lastAtom, context, dependencies);
  if (atoms.length < 2) {
    return lastDescription;
  }

  const penultimateAtom = atoms[atoms.length - 2];
  const penultimateDescription = buildDefaultAtomName(penultimateAtom, context, dependencies);

  return `${lastDescription} (${penultimateDescription})`;
}

export function buildPlaceholderName(context: ExpressionsContext): string {
  // We should not take into account immediate expressions.
  const localExpressions = context.expressions.filter((expression) => expression.name.startsWith('local:'));
  return `Expression ${localExpressions.length + 1}`;
}

function buildDefaultAtomName(
  atom: ClientAtom,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  const transformer = getAtomTransformer(atom);
  return transformer.getDefaultName(atom, context, dependencies);
}

export function inferFunctionStartType(atom: Atom): InferredType {
  const transformer = getAtomTransformer(atom);
  return transformer.inferFunctionStartType(atom.previousInfer);
}

export function getParameters(atom: Atom) {
  const transformer = getAtomTransformer(atom);
  return transformer.getParameters();
}

export function getAtomDefaultParameters(atom: Atom, context?: ExpressionsContext) {
  const transformer = getAtomTransformer(atom);
  return transformer.getAtomDefaultParameters(context);
}

export function filterStringExpressions(
  expressions: ReadonlyArray<Expression> | undefined,
  acceptList: boolean,
) {
  return filterExpressionsWithType(expressions, StringLikeResults, acceptList);
}

export function filterTeammateExpressions(
  expressions: ReadonlyArray<Expression> | undefined,
  acceptList: boolean,
) {
  return filterExpressionsWithType(expressions, [TeammateResult], acceptList);
}

export function filterInboxExpressions(
  expressions: ReadonlyArray<Expression> | undefined,
  acceptList: boolean,
) {
  return filterExpressionsWithType(expressions, [InboxResult], acceptList);
}

export function filterTagExpressions(
  expressions: ReadonlyArray<Expression> | undefined,
  acceptList: boolean,
) {
  return filterExpressionsWithType(expressions, [TagResult], acceptList);
}

export function getAtomResultType(atom: ClientAtom) {
  return getResultType(atom.inferredType);
}

export function getPreviousAtomResultType(atom: ClientAtom) {
  return getResultType(atom.previousInfer);
}

function filterExpressionsWithType(
  expressions: ReadonlyArray<Expression> | undefined,
  acceptedTypes: ReadonlyArray<InferredType>,
  acceptList: boolean,
) {
  if (!expressions) {
    return [];
  }

  function acceptedTypesIncludesInferredType(inferredType: Expression['inferredType']): boolean {
    if (acceptedTypes.some((acceptedType) => areInferredTypesEqual(acceptedType, inferredType))) {
      return true;
    }

    if (acceptList && isListType(inferredType)) {
      return acceptedTypesIncludesInferredType(inferredType.itemType);
    }

    return false;
  }

  return expressions.filter((expression) => acceptedTypesIncludesInferredType(expression.inferredType));
}

export function getMethodCategoryForAtom(atom: Atom) {
  const transformer = getAtomTransformer(atom);
  return transformer.category;
}

export function buildImmediateExpressions(inferredType: InferredType): ReadonlyArray<Expression> {
  const resultType = getResultType(inferredType);

  return (resultType.immediate || []).map((propertyId) => ({
    id: brandExpressionId(propertyId),
    name: getPropertyLabel(propertyId),
    defaultName: getPropertyLabel(propertyId),
    atoms: [],
    inferredType: resultType.properties[propertyId],
  }));
}

export function filterOutPreviousExpressions(
  expressions: ReadonlyArray<Expression>,
  atom: ClientAtom | ClientFunctionAtom | undefined,
) {
  // Inside an expression, you can only reference previous expressions.
  const maxIndex = getExpressionsMaxIndexByAtom(expressions, atom);
  return expressions.filter((_expression, index) => index < maxIndex);
}

export function filterAllowedExpressions(
  expressions: ReadonlyArray<Expression>,
  atom: ClientAtom | ClientFunctionAtom | undefined,
  allowList: boolean,
  expectedReturnType?: string,
) {
  return filterOutPreviousExpressions(expressions, atom).filter((expression) => {
    const {inferredType} = expression;

    if (!expectedReturnType) {
      return true;
    }

    const isCorrectType = inferredType.type === expectedReturnType;
    const isCorrectListType = isListType(inferredType) && inferredType.itemType.type === expectedReturnType;

    return isCorrectType || (allowList && isCorrectListType);
  });
}
